/* Plain helpers for the fund portal (kept apart from the components so fast
 * refresh stays intact). */

export function formatPortalDate(
  value: string | Date | undefined,
  style: "long" | "short" = "long"
): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: style === "long" ? "long" : "short",
    day: "numeric",
  });
}

export type IntakeState = "open" | "upcoming" | "closed" | "rolling";

/**
 * Where the intake window stands today. A portal with no dates takes
 * applications on a rolling basis.
 */
export function intakeState(
  start?: string | Date,
  end?: string | Date,
  now: Date = new Date()
): IntakeState {
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  if (endDate && !Number.isNaN(endDate.getTime()) && now > endDate) return "closed";
  if (startDate && !Number.isNaN(startDate.getTime()) && now < startDate) return "upcoming";
  if (!startDate && !endDate) return "rolling";
  return "open";
}

function formatChequeSize(value: number, currency?: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

/** "$50,000 to $250,000", "$50,000+", "Up to $250,000", or null when unset. */
export function formatCapitalRange(
  min?: number,
  max?: number,
  currency?: string
): string | null {
  const hasMin = !!min && min > 0;
  const hasMax = !!max && max > 0;
  if (hasMin && hasMax) {
    return `${formatChequeSize(min, currency)} to ${formatChequeSize(max, currency)}`;
  }
  if (hasMin) return `${formatChequeSize(min, currency)}+`;
  if (hasMax) return `Up to ${formatChequeSize(max, currency)}`;
  return null;
}

function looksLikeHtml(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value);
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

function parseResources(prize: string): string[] {
  // Split by newlines or bullet points
  return prize
    .split(/[\n\r]+|(?:^|\s)[-•]\s*/)
    .map((item) => item.replace(/[^\S\x20]/g, " ").replace(/\s+/g, " ").trim())
    .filter((item) => item.length > 0);
}

/**
 * The fund's "resources" field, which may be rich-text HTML (from the
 * workspace settings editor) or legacy plain text split by newlines / bullet
 * chars. Numbers and currency fragments left over from old free-text entries
 * are dropped.
 */
export function parseSupport(prize?: string): {
  isHtml: boolean;
  resources: string[];
  hasResources: boolean;
} {
  const isHtml = prize ? looksLikeHtml(prize) : false;
  const resources =
    prize && !isHtml
      ? parseResources(prize).filter(
          (item) =>
            item.length > 2 &&
            !/^\d[\d,.\s]*$/.test(item) &&
            !/^[$€£¥]\s*\d/.test(item) &&
            item !== "0" &&
            isNaN(Number(item))
        )
      : [];
  const hasResources = isHtml
    ? stripHtml(prize ?? "").length > 0
    : resources.length > 0;
  return { isHtml, resources, hasResources };
}

/** Rich-text HTML as plain words: block boundaries become spaces. */
export function plainTextFromHtml(html?: string): string {
  if (!html) return "";
  const spaced = html.replace(/<\/(p|li|div|h[1-6])>|<br\s*\/?>/gi, " ");
  if (typeof DOMParser === "undefined") {
    return stripHtml(spaced).replace(/\s+/g, " ").trim();
  }
  const text =
    new DOMParser().parseFromString(spaced, "text/html").body.textContent ?? "";
  return text.replace(/\s+/g, " ").trim();
}

/**
 * The opening sentence of a fund's description, for the portal's cover.
 * Capped so a description written as one long paragraph still reads as a lede.
 */
export function firstSentence(html?: string, max = 200): string | null {
  const text = plainTextFromHtml(html).replace(/^[\\/\s]+/, "");
  if (!text) return null;
  const match = text.match(/^(.+?[.!?])(\s|$)/);
  const sentence = (match ? match[1] : text).trim();
  if (sentence.length <= max) return sentence;
  const cut = sentence.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:]$/, "")}…`;
}
